import * as React from 'react';
import type { FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { ContentContextProvider, FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import type { Logger } from '@feature-hub/logger';
import type {
  ContentService,
  RenderModeService,
  FootnoteReferenceService,
  TrackingService,
  AsyncSsrManager,
  SerializedStateManager,
  LocaleService,
  VideoService,
  OneGraphService,
} from './feature-service-types';

import type { AccordionContent } from '../types';
import { AccordionLoader } from './components/Accordion/Accordion';
import ContextProvider from './Context';
import { getDesignLibrary } from './components/utils/alpha-helpers';

interface FeatureServiceDependencies extends FeatureServices {
  readonly 'audi-content-service': ContentService;
  readonly 'audi-footnote-reference-service': FootnoteReferenceService;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-video-service': VideoService;
  readonly 'locale-service': LocaleService;
  readonly 'audi-render-mode-service': RenderModeService;
  readonly 's2:logger'?: Logger;
  readonly 'onegraph-service': OneGraphService;
}

export default {
  dependencies: {
    externals: {
      react: '*',
      'react-dom': '*',
      'styled-components': '*',
      '@feature-hub/react': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-video-service': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },
  create: ({
    featureServices: {
      'audi-content-service': contentService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      'locale-service': localeService,
      'audi-render-mode-service': renderModeService,
      's2:async-ssr-manager': asyncSSRManager,
      'audi-video-service': videoService,
      'audi-tracking-service': trackingService,
      'onegraph-service': oneGraphService,
      's2:logger': loggerService,
    },
    config,
    featureAppId,
    featureAppName,
  }: FeatureAppEnvironment<FeatureServiceDependencies, AccordionContent>) => {
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    const { OneGraphProvider } = oneGraphService;
    const requiredServiceForOneGraph = {
      'onegraph-service': oneGraphService,
      's2:async-ssr-manager': asyncSSRManager,
      'locale-service': localeService,
    };
    return {
      render: () => (
        <ContentContextProvider contentService={contentService}>
          <OneGraphProvider featureServices={requiredServiceForOneGraph}>
            <FootnoteContextProvider
              // eslint-disable-next-line max-len
              footnoteReferenceService={footnoteReferenceServiceScopeManager?.getDefaultScopeRefService()}
            >
              <UniversalEditorProvider
                contentService={contentService}
                renderModeService={renderModeService}
              >
                <ContextProvider
                  featureAppEnv={{ featureAppId, featureAppName }}
                  localeService={localeService}
                  loggerService={loggerService}
                  trackingService={trackingService}
                  videoService={videoService}
                  oneGraphService={oneGraphService}
                >
                  <AccordionLoader
                    initialContent={config}
                    meta={{ featureAppId }}
                    loggerService={loggerService}
                  />
                </ContextProvider>
              </UniversalEditorProvider>
            </FootnoteContextProvider>
          </OneGraphProvider>
        </ContentContextProvider>
      ),
    };
  },
};
